import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp,
  faFilter,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { config } from "../../config";

function Filters({ results, setFilteredResults }) {
  const [originalResults] = useState(results || []);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [sortOption, setSortOption] = useState("recommended");
  const [direct, setDirect] = useState(false);
  const [oneStop, setOneStop] = useState(false);
  const [twoPlusStops, setTwoPlusStops] = useState(false);

  const [departureTimeFrom, setDepartureTimeFrom] = useState("00");
  const [departureTimeTo, setDepartureTimeTo] = useState("23");
  const [arrivalTimeFrom, setArrivalTimeFrom] = useState("00");
  const [arrivalTimeTo, setArrivalTimeTo] = useState("23");

  // Airlines (checkboxes)
  const [selectedAirlines, setSelectedAirlines] = useState([]);

  // Airports (checkboxes)
  // We'll separate "origin" and "destination" so the user can filter each individually.
  const [selectedOrigins, setSelectedOrigins] = useState([]);
  const [selectedDestinations, setSelectedDestinations] = useState([]);

  // Baggage (checkboxes)
  const [selectedBaggageTypes, setSelectedBaggageTypes] = useState([]);

  // For collapsible sections
  const [isStopsVisible, setStopsVisible] = useState(true);
  const [isTimesVisible, setTimesVisible] = useState(false);
  const [isAirlinesVisible, setAirlinesVisible] = useState(false);
  const [isAirportsVisible, setAirportsVisible] = useState(false);
  const [isBaggageVisible, setBaggageVisible] = useState(false);
  const [isSortVisible, setSortVisible] = useState(true);

  // Move the gathering of unique values inside the useEffect
  useEffect(() => {
    if (!results?.length) {
      setFilteredResults([]);
      return;
    }

    let filtered = [...results];

    // Apply all filters except airlines first
    // === Stops filter ===
    const anyStopFilter = direct || oneStop || twoPlusStops;
    if (anyStopFilter) {
      filtered = filtered.filter((flight) => {
        const segCount = flight.slices?.[0]?.segments?.length || 0;
        const passesDirect = direct && segCount === 1;
        const passesOneStop = oneStop && segCount <= 2;
        const passesTwoPlus = twoPlusStops && segCount >= 3;
        return passesDirect || passesOneStop || passesTwoPlus;
      });
    }

    // === Times filter ===
    // We check departureTimeFrom/To with flight.slices[0].segments[0].departing_at
    // and arrivalTimeFrom/To with flight.slices[0].segments[last].arriving_at
    // We only do the filter if user changed from defaults (00 - 23).

    filtered = filtered.filter((flight) => {
      const segs = flight.slices?.[0]?.segments;
      if (!segs || segs.length === 0) return false;

      const firstSeg = segs[0];
      const lastSeg = segs[segs.length - 1];

      // Convert the flight's departure/arrival to total minutes from midnight
      const depDate = new Date(firstSeg.departing_at);
      const depHour = depDate.getHours();
      const depMinute = depDate.getMinutes();
      const depTotal = depHour * 60 + depMinute;

      const arrDate = new Date(lastSeg.arriving_at);
      const arrHour = arrDate.getHours();
      const arrMinute = arrDate.getMinutes();
      const arrTotal = arrHour * 60 + arrMinute;

      // Convert the user's selected hours to total minutes
      const depFromTotal = parseInt(departureTimeFrom, 10) * 60;
      const depToTotal = parseInt(departureTimeTo, 10) * 60;
      const arrFromTotal = parseInt(arrivalTimeFrom, 10) * 60;
      const arrToTotal = parseInt(arrivalTimeTo, 10) * 60;

      const depPasses = depTotal >= depFromTotal && depTotal <= depToTotal;
      const arrPasses = arrTotal >= arrFromTotal && arrTotal <= arrToTotal;

      return depPasses && arrPasses;
    });

    // Get available airlines from currently filtered results
    const availableAirlines = Array.from(
      new Set(filtered.map((f) => f.owner?.name).filter(Boolean))
    );

    // Only apply airline filter if selected airlines are still available
    const validSelectedAirlines = selectedAirlines.filter((airline) =>
      availableAirlines.includes(airline)
    );

    if (validSelectedAirlines.length > 0) {
      filtered = filtered.filter((flight) =>
        validSelectedAirlines.includes(flight.owner?.name)
      );
    }

    // Update airline options in the UI
    setAirlineNames(availableAirlines);

    // === Airports filter ===
    // If user picks origin(s), flight's first segment origin name must match one of them
    if (selectedOrigins.length > 0) {
      filtered = filtered.filter((flight) => {
        const origName = flight.slices?.[0]?.segments?.[0]?.origin?.name || "";
        return selectedOrigins.includes(origName);
      });
    }
    // If user picks destination(s), flight's last segment destination name must match one of them
    if (selectedDestinations.length > 0) {
      filtered = filtered.filter((flight) => {
        const segs = flight.slices?.[0]?.segments || [];
        const destName = segs[segs.length - 1]?.destination?.name || "";
        return selectedDestinations.includes(destName);
      });
    }

    // === Baggage filter ===
    // If user picks baggage types, flight must have them (in first seg's first passenger).
    if (selectedBaggageTypes.length > 0) {
      filtered = filtered.filter((flight) => {
        const segs = flight.slices?.[0]?.segments;
        if (!segs?.[0]?.passengers?.[0]?.baggages?.length) return false;
        const baggageArray = segs[0].passengers[0].baggages.filter(
          (b) => b.quantity > 0
        );
        const flightBaggageTypes = baggageArray.map((b) => b.type);
        // Must contain ALL of user's selected types? or ANY?
        // Typically "ANY" is used, but you can pick your logic. Let's assume ANY:
        return selectedBaggageTypes.every((type) =>
          flightBaggageTypes.includes(type)
        );
      });
    }

    // === Sorting ===
    if (sortOption === "cheapest") {
      filtered.sort((a, b) => {
        const priceA = parseFloat(a.total_amount || "9999999");
        const priceB = parseFloat(b.total_amount || "9999999");
        return priceA - priceB; // ascending
      });
    } else if (sortOption === "quickest") {
      // sum all slices' durations to minutes
      const flightDuration = (f) => {
        if (!f.slices) return 999999;
        let totalMinutes = 0;
        f.slices.forEach((sl) => {
          // Duffel durations are typically "PT1H10M"
          const matchH = sl.duration.match(/(\d+)H/)?.[1] || 0;
          const matchM = sl.duration.match(/(\d+)M/)?.[1] || 0;
          totalMinutes += parseInt(matchH, 10) * 60 + parseInt(matchM, 10);
        });
        return totalMinutes;
      };
      filtered.sort((a, b) => flightDuration(a) - flightDuration(b));
    } else {
      // recommended => revert to original order
      // We can do this by mapping each flight to its index in originalResults
      // Then sort by that index
      filtered.sort(
        (a, b) => originalResults.indexOf(a) - originalResults.indexOf(b)
      );
    }

    setFilteredResults(filtered);
  }, [
    results,
    setFilteredResults,
    direct,
    oneStop,
    twoPlusStops,
    departureTimeFrom,
    departureTimeTo,
    arrivalTimeFrom,
    arrivalTimeTo,
    selectedAirlines,
    selectedOrigins,
    selectedDestinations,
    selectedBaggageTypes,
    sortOption,
    originalResults,
  ]);

  // Add state for dynamic airline names
  const [airlineNames, setAirlineNames] = useState([]);

  // === 4) Render UI ===
  // Similar to your HotelFilters structure
  return (
    <>
      {/* Mobile Filter Button */}
      <div className="md:hidden fixed bottom-4 right-4 z-50">
        <button
          onClick={() => setIsMobileFilterOpen(!isMobileFilterOpen)}
          className={`${config.primaryColorBg} text-white p-4 rounded-full shadow-lg`}
        >
          <FontAwesomeIcon icon={isMobileFilterOpen ? faTimes : faFilter} />
        </button>
      </div>

      {/* Filters Panel */}
      <div
        className={`bg-white rounded-lg p-4 space-y-4 ${
          isMobileFilterOpen
            ? "fixed inset-0 z-50 overflow-y-auto"
            : "hidden md:block"
        }`}
      >
        {/* Mobile Close Button */}
        {isMobileFilterOpen && (
          <div className="flex justify-end md:hidden">
            <button
              onClick={() => setIsMobileFilterOpen(false)}
              className="p-2 text-gray-500 hover:text-gray-700"
            >
              <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Sort Options */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-900">Sort</h3>
            <button onClick={() => setSortVisible(!isSortVisible)}>
              <FontAwesomeIcon
                icon={isSortVisible ? faChevronUp : faChevronDown}
              />
            </button>
          </div>
          {isSortVisible && (
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="sortOption"
                  value="recommended"
                  checked={sortOption === "recommended"}
                  onChange={(e) => setSortOption(e.target.value)}
                />
                <span className="ml-2 text-sm text-gray-700">Recommended</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="sortOption"
                  value="cheapest"
                  checked={sortOption === "cheapest"}
                  onChange={(e) => setSortOption(e.target.value)}
                />
                <span className="ml-2 text-sm text-gray-700">Cheapest</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="sortOption"
                  value="quickest"
                  checked={sortOption === "quickest"}
                  onChange={(e) => setSortOption(e.target.value)}
                />
                <span className="ml-2 text-sm text-gray-700">Quickest</span>
              </label>
            </div>
          )}
        </div>

        {/* Stops */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-900">Stops</h3>
            <button onClick={() => setStopsVisible(!isStopsVisible)}>
              <FontAwesomeIcon
                icon={isStopsVisible ? faChevronUp : faChevronDown}
              />
            </button>
          </div>
          {isStopsVisible && (
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={direct}
                  onChange={(e) => setDirect(e.target.checked)}
                />
                <span className="ml-2 text-sm text-gray-700">Direct</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={oneStop}
                  onChange={(e) => setOneStop(e.target.checked)}
                />
                <span className="ml-2 text-sm text-gray-700">Up to 1 stop</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={twoPlusStops}
                  onChange={(e) => setTwoPlusStops(e.target.checked)}
                />
                <span className="ml-2 text-sm text-gray-700">
                  2 or more stops
                </span>
              </label>
            </div>
          )}
        </div>

        {/* Times */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-900">Times</h3>
            <button onClick={() => setTimesVisible(!isTimesVisible)}>
              <FontAwesomeIcon
                icon={isTimesVisible ? faChevronUp : faChevronDown}
              />
            </button>
          </div>
          {isTimesVisible && (
            <div className="space-y-2">
              <p className="text-sm font-semibold">Departure:</p>
              <div className="flex items-center gap-2">
                <select
                  value={departureTimeFrom}
                  onChange={(e) => setDepartureTimeFrom(e.target.value)}
                  className="border p-1 rounded"
                >
                  {Array.from({ length: 24 }, (_, i) => i).map((hour) => (
                    <option key={hour} value={String(hour).padStart(2, "0")}>
                      {String(hour).padStart(2, "0")}:00
                    </option>
                  ))}
                </select>
                <span className="text-sm">to</span>
                <select
                  value={departureTimeTo}
                  onChange={(e) => setDepartureTimeTo(e.target.value)}
                  className="border p-1 rounded"
                >
                  {Array.from({ length: 24 }, (_, i) => i).map((hour) => (
                    <option key={hour} value={String(hour).padStart(2, "0")}>
                      {String(hour).padStart(2, "0")}:00
                    </option>
                  ))}
                </select>
              </div>

              <p className="text-sm font-semibold">Arrival:</p>
              <div className="flex items-center gap-2">
                <select
                  value={arrivalTimeFrom}
                  onChange={(e) => setArrivalTimeFrom(e.target.value)}
                  className="border p-1 rounded"
                >
                  {Array.from({ length: 24 }, (_, i) => i).map((hour) => (
                    <option key={hour} value={String(hour).padStart(2, "0")}>
                      {String(hour).padStart(2, "0")}:00
                    </option>
                  ))}
                </select>
                <span className="text-sm">to</span>
                <select
                  value={arrivalTimeTo}
                  onChange={(e) => setArrivalTimeTo(e.target.value)}
                  className="border p-1 rounded"
                >
                  {Array.from({ length: 24 }, (_, i) => i).map((hour) => (
                    <option key={hour} value={String(hour).padStart(2, "0")}>
                      {String(hour).padStart(2, "0")}:00
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Airlines */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-900">Airlines</h3>
            <button onClick={() => setAirlinesVisible(!isAirlinesVisible)}>
              <FontAwesomeIcon
                icon={isAirlinesVisible ? faChevronUp : faChevronDown}
              />
            </button>
          </div>
          {isAirlinesVisible && (
            <div className="space-y-2">
              {airlineNames.map((airline) => (
                <label key={airline} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={selectedAirlines.includes(airline)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedAirlines([...selectedAirlines, airline]);
                      } else {
                        setSelectedAirlines(
                          selectedAirlines.filter((a) => a !== airline)
                        );
                      }
                    }}
                  />
                  <span className="ml-2 text-sm text-gray-700">{airline}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Airports */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-900">Airports</h3>
            <button onClick={() => setAirportsVisible(!isAirportsVisible)}>
              <FontAwesomeIcon
                icon={isAirportsVisible ? faChevronUp : faChevronDown}
              />
            </button>
          </div>
          {isAirportsVisible && (
            <>
              <div className="mb-2">
                <p className="font-medium text-sm">Origin</p>
                <div className="space-y-2">
                  {Array.from(
                    new Set(
                      results
                        .map((f) => f.slices?.[0]?.segments?.[0]?.origin?.name)
                        .filter(Boolean)
                    )
                  ).map((origin) => (
                    <label key={origin} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedOrigins.includes(origin)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedOrigins([...selectedOrigins, origin]);
                          } else {
                            setSelectedOrigins(
                              selectedOrigins.filter((o) => o !== origin)
                            );
                          }
                        }}
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        {origin}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <p className="font-medium text-sm">Destination</p>
                <div className="space-y-2">
                  {Array.from(
                    new Set(
                      results
                        .map((f) => {
                          const segments = f.slices?.[0]?.segments;
                          if (!segments?.length) return null;
                          return segments[segments.length - 1]?.destination
                            ?.name;
                        })
                        .filter(Boolean)
                    )
                  ).map((dest) => (
                    <label key={dest} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedDestinations.includes(dest)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedDestinations([
                              ...selectedDestinations,
                              dest,
                            ]);
                          } else {
                            setSelectedDestinations(
                              selectedDestinations.filter((d) => d !== dest)
                            );
                          }
                        }}
                      />
                      <span className="ml-2 text-sm text-gray-700">{dest}</span>
                    </label>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Baggage */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-900">Baggage</h3>
            <button onClick={() => setBaggageVisible(!isBaggageVisible)}>
              <FontAwesomeIcon
                icon={isBaggageVisible ? faChevronUp : faChevronDown}
              />
            </button>
          </div>
          {isBaggageVisible && (
            <div className="space-y-2">
              {Array.from(
                new Set(
                  results.flatMap((f) => {
                    const segments = f.slices?.[0]?.segments;
                    if (!segments?.[0]?.passengers?.[0]?.baggages?.length)
                      return [];
                    return segments[0].passengers[0].baggages
                      .filter((b) => b.quantity > 0)
                      .map((b) => b.type);
                  })
                )
              ).map((bType) => (
                <label key={bType} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={selectedBaggageTypes.includes(bType)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedBaggageTypes([
                          ...selectedBaggageTypes,
                          bType,
                        ]);
                      } else {
                        setSelectedBaggageTypes(
                          selectedBaggageTypes.filter((t) => t !== bType)
                        );
                      }
                    }}
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    {bType === "carry_on"
                      ? "Carry on"
                      : bType === "checked"
                      ? "Checked"
                      : bType}
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default Filters;
